import React, { useState, useEffect, useRef } from 'react';
import reportingFinancierApi from '../services/reporting-financier';
import useReportingData from '../hooks/useReportingData';
import UpdatingOverlay from './UpdatingOverlay';
import './ReportingPdrApv.css';
import './ReportingVN.css';
import './ReportingMarche.css';

// Reporting Financier — onglet « Marché » (immatriculations).
//
// Source : base Google Sheet `gsheet_data_immat` (feuille DATA-IMMAT), via
// GET /reporting-financier/marche/{kpis,dimensions}. Analyse par période :
//   - parts de marché par marque
//   - parts de marché par groupe
//   - volume par type de client
//   - vue par département (volume + part + marque leader)
//   - évolution mensuelle (parts de marché % OU volume par marque)
//
// La dimension structurante est le DÉPARTEMENT (filtre local multi). Filtre
// VP/VUL local. La toolbar concession du shell n'est pas pertinente ici (données
// marché, pas nos ventes) : elle est ignorée. La période vient du shell.

const fmtNumber = (v) => (v == null ? '—' : new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 0 }).format(v));
const fmtPct = (v) => (v == null ? '—' : `${new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 1 }).format(v)} %`);

// ─── Multi-sélection département (réutilise les styles rf-ms-* du shell) ──────
function DeptSelect({ options, selected, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);
  const toggle = (val) => {
    if (selected.includes(val)) onChange(selected.filter(v => v !== val));
    else onChange([...selected, val]);
  };
  const label = selected.length === 0
    ? 'Tous départements'
    : selected.length === 1 ? selected[0] : `${selected.length} départements`;
  return (
    <div className="rf-ms" ref={ref}>
      <button type="button" className={`page-select rf-ms-btn ${selected.length ? 'active-filter' : ''}`} onClick={() => setOpen(o => !o)}>
        <span className="rf-ms-label">{label}</span>
        <span className="rf-ms-caret">▾</span>
      </button>
      {open && (
        <div className="rf-ms-panel">
          <button type="button" className="rf-ms-all" onClick={() => onChange([])}>Tous départements</button>
          <div className="rf-ms-options">
            {options.map(opt => (
              <label key={opt} className="rf-ms-option">
                <input type="checkbox" checked={selected.includes(opt)} onChange={() => toggle(opt)} />
                <span>{opt}</span>
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// Badge d'évolution vs N-1 (▲ vert hausse / ▼ rouge baisse). unit : '%' ou 'pt'.
function DeltaBadge({ value, unit = '%' }) {
  if (value == null) return <span className="rpa-delta-empty">—</span>;
  const cls = value > 0 ? 'rpa-delta-up' : value < 0 ? 'rpa-delta-down' : 'rpa-delta-neutral';
  const arrow = value > 0 ? '▲' : value < 0 ? '▼' : '◆';
  const sign = value > 0 ? '+' : '';
  return (
    <span className={`rpa-delta ${cls}`}>
      <span className="rpa-delta-arrow">{arrow}</span>{sign}{value.toFixed(1)} {unit}
    </span>
  );
}

// Badge d'écart de volume vs N-1 en unités (entier signé + flèche).
function CountDeltaBadge({ value }) {
  if (value == null) return <span className="rpa-delta-empty">—</span>;
  const cls = value > 0 ? 'rpa-delta-up' : value < 0 ? 'rpa-delta-down' : 'rpa-delta-neutral';
  const arrow = value > 0 ? '▲' : value < 0 ? '▼' : '◆';
  const sign = value > 0 ? '+' : '';
  return (
    <span className={`rpa-delta ${cls}`}>
      <span className="rpa-delta-arrow">{arrow}</span>{sign}{fmtNumber(value)}
    </span>
  );
}

// Cellule Volume : nombre + badge d'évolution N-1 (unités) accolé. `delta`
// undefined → pas de badge (ex. lignes sans comparatif).
function VolCell({ volume, delta }) {
  return (
    <span className="mar-evol-cell">
      {fmtNumber(volume)}
      {delta !== undefined && <CountDeltaBadge value={delta} />}
    </span>
  );
}

// Cellule Part : barre (ou texte si bar=false) + badge d'évolution N-1 (pts) accolé.
function PartCell({ part, delta, bar = true }) {
  return (
    <span className="mar-evol-cell">
      {bar ? (
        <span className="rvn-part">
          <span className="rvn-part-bar" style={{ width: `${Math.min(part || 0, 100).toFixed(1)}%` }} />
          <span className="rvn-part-txt">{fmtPct(part)}</span>
        </span>
      ) : fmtPct(part)}
      {delta !== undefined && <DeltaBadge value={delta} unit="pt" />}
    </span>
  );
}

// Carte KPI plate (réutilise rpa-hero-card). `sublabel` accepte du JSX.
function KpiHero({ label, value, sublabel }) {
  return (
    <div className="rpa-hero-card">
      <div className="rpa-hero-label">{label}</div>
      <div className="rpa-hero-value">{value}</div>
      <div className="rpa-hero-foot"><span className="rpa-hero-sublabel">{sublabel || ' '}</span></div>
    </div>
  );
}

// Picto d'évolution de rang vs N-1 (placé à côté de la marque / du groupe).
// delta_rang = rang_n1 − rang : >0 = a gagné des places (▲), <0 = en a perdu (▼).
function RankBadge({ row }) {
  if (!row || row.rang == null) return null; // pas de classement (type de client, ou ligne « — »)
  if (row.rang_n1 == null) {
    return <span className="mar-rank mar-rank-new" title="Nouvelle entrée vs N-1">nouv.</span>;
  }
  const d = row.delta_rang;
  if (d === 0) {
    return <span className="mar-rank mar-rank-eq" title={`Rang ${row.rang} · stable vs N-1`}>=</span>;
  }
  const up = d > 0;
  return (
    <span
      className={`mar-rank ${up ? 'mar-rank-up' : 'mar-rank-down'}`}
      title={`Rang ${row.rang_n1} → ${row.rang} vs N-1`}
    >
      {up ? '▲' : '▼'} {up ? '+' : ''}{d}
    </span>
  );
}


// Tableau de ventilation à drill-down sur 2 niveaux (parent ▸ enfant ▸ petit-enfant).
//   entityLabel  : en-tête 1re colonne (« Groupe », « Marque »…)
//   childKey     : array enfant niveau 1 ('marques' | 'modeles')
//   grandChildKey: array enfant niveau 2 ('clients')
//   withRank     : caret + n° de classement (#) + picto d'évolution de rang au niveau parent
function Drilldown3Table({ rows, entityLabel, childKey, grandChildKey, withRank = false }) {
  const [open, setOpen] = useState({});      // parents dépliés
  const [openC, setOpenC] = useState({});    // enfants dépliés (clé parent∥enfant)
  if (!rows || rows.length === 0) return <div className="rpa-empty">Aucune immatriculation sur la période.</div>;
  const total = rows.reduce((s, r) => s + (r.volume || 0), 0);
  const totalN1 = rows.reduce((s, r) => s + (r.volume_n1 || 0), 0);
  const totalDeltaVol = total - totalN1;
  const toggle = (lbl) => setOpen(o => ({ ...o, [lbl]: !o[lbl] }));
  const toggleC = (key) => setOpenC(o => ({ ...o, [key]: !o[key] }));
  return (
    <div className="rvn-table-wrap">
      <table className="rvn-table">
        <thead>
          <tr>
            <th className="mar-caret-col"></th>
            {withRank && <th className="mar-rang-col">#</th>}
            <th>{entityLabel}</th>
            <th className="rvn-num">Volume <span className="mar-th-sub">· évol. N-1</span></th>
            <th className="rvn-num">Part de marché <span className="mar-th-sub">· évol. N-1</span></th>
          </tr>
        </thead>
        <tbody>
          {rows.map((p, i) => {
            const children = p[childKey] || [];
            const isOpen = !!open[p.label];
            return (
              <React.Fragment key={i}>
                <tr className={`mar-groupe-row ${isOpen ? 'mar-groupe-open' : ''}`} onClick={() => children.length && toggle(p.label)}>
                  <td className="mar-caret">{children.length ? (isOpen ? '▾' : '▸') : ''}</td>
                  {withRank && <td className="mar-rang-num">{p.rang ?? ''}</td>}
                  <td className="rvn-marque-cell"><span className="mar-label-txt">{p.label}</span>{withRank && <RankBadge row={p} />}</td>
                  <td className="rvn-num"><VolCell volume={p.volume} delta={p.delta_volume} /></td>
                  <td className="rvn-num"><PartCell part={p.part} delta={p.delta_part} /></td>
                </tr>
                {isOpen && children.map((c, j) => {
                  const grandChildren = c[grandChildKey] || [];
                  const cKey = p.label + '' + c.label;
                  const cOpen = !!openC[cKey];
                  return (
                    <React.Fragment key={j}>
                      <tr className={`mar-submarque-row ${cOpen ? 'mar-submarque-open' : ''}`} onClick={() => grandChildren.length && toggleC(cKey)}>
                        <td className="mar-caret mar-caret-2">{grandChildren.length ? (cOpen ? '▾' : '▸') : ''}</td>
                        {withRank && <td></td>}
                        <td className="mar-submarque-label">{c.label}</td>
                        <td className="rvn-num"><VolCell volume={c.volume} delta={c.delta_volume} /></td>
                        <td className="rvn-num"><PartCell part={c.part} delta={c.delta_part} bar={false} /></td>
                      </tr>
                      {cOpen && grandChildren.map((gc, k) => (
                        <tr key={k} className="mar-subclient-row">
                          <td></td>
                          {withRank && <td></td>}
                          <td className="mar-subclient-label">{gc.label}</td>
                          <td className="rvn-num"><VolCell volume={gc.volume} delta={gc.delta_volume} /></td>
                          <td className="rvn-num"><PartCell part={gc.part} delta={gc.delta_part} bar={false} /></td>
                        </tr>
                      ))}
                    </React.Fragment>
                  );
                })}
              </React.Fragment>
            );
          })}
        </tbody>
        <tfoot>
          <tr className="rvn-total-row">
            <td></td>
            {withRank && <td></td>}
            <td>Total</td>
            <td className="rvn-num"><VolCell volume={total} delta={totalDeltaVol} /></td>
            <td className="rvn-num">100 %</td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

// Tableau de ventilation à drill-down sur 1 niveau (parent ▸ enfants).
//   entityLabel : en-tête de la 1re colonne (« Marque », « Type de client »…)
//   childKey    : clé de l'array d'enfants sur chaque ligne ('clients' | 'marques')
//   withRank    : caret + numéro de classement (#) à gauche + picto d'évolution de rang
function Drilldown2Table({ rows, entityLabel, childKey, withRank = false }) {
  const [open, setOpen] = useState({});
  if (!rows || rows.length === 0) return <div className="rpa-empty">Aucune immatriculation sur la période.</div>;
  const total = rows.reduce((s, r) => s + (r.volume || 0), 0);
  const totalN1 = rows.reduce((s, r) => s + (r.volume_n1 || 0), 0);
  const totalDeltaVol = total - totalN1;
  const toggle = (lbl) => setOpen(o => ({ ...o, [lbl]: !o[lbl] }));
  return (
    <div className="rvn-table-wrap">
      <table className="rvn-table">
        <thead>
          <tr>
            <th className="mar-caret-col"></th>
            {withRank && <th className="mar-rang-col">#</th>}
            <th>{entityLabel}</th>
            <th className="rvn-num">Volume <span className="mar-th-sub">· évol. N-1</span></th>
            <th className="rvn-num">Part de marché <span className="mar-th-sub">· évol. N-1</span></th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => {
            const children = r[childKey] || [];
            const isOpen = !!open[r.label];
            return (
              <React.Fragment key={i}>
                <tr className={`mar-groupe-row ${isOpen ? 'mar-groupe-open' : ''}`} onClick={() => children.length && toggle(r.label)}>
                  <td className="mar-caret">{children.length ? (isOpen ? '▾' : '▸') : ''}</td>
                  {withRank && <td className="mar-rang-num">{r.rang ?? ''}</td>}
                  <td className="rvn-marque-cell"><span className="mar-label-txt">{r.label}</span>{withRank && <RankBadge row={r} />}</td>
                  <td className="rvn-num"><VolCell volume={r.volume} delta={r.delta_volume} /></td>
                  <td className="rvn-num"><PartCell part={r.part} delta={r.delta_part} /></td>
                </tr>
                {isOpen && children.map((c, k) => (
                  <tr key={k} className="mar-submarque-row" style={{ cursor: 'default' }}>
                    <td></td>
                    {withRank && <td></td>}
                    <td className="mar-submarque-label">{c.label}</td>
                    <td className="rvn-num"><VolCell volume={c.volume} delta={c.delta_volume} /></td>
                    <td className="rvn-num"><PartCell part={c.part} delta={c.delta_part} bar={false} /></td>
                  </tr>
                ))}
              </React.Fragment>
            );
          })}
        </tbody>
        <tfoot>
          <tr className="rvn-total-row">
            <td></td>
            {withRank && <td></td>}
            <td>Total</td>
            <td className="rvn-num"><VolCell volume={total} delta={totalDeltaVol} /></td>
            <td className="rvn-num">100 %</td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

// Vue par département : volume + part + marque leader (toujours affichée).
function DepartementTable({ rows }) {
  if (!rows || rows.length === 0) return <div className="rpa-empty">Aucune immatriculation sur la période.</div>;
  return (
    <div className="rvn-table-wrap">
      <table className="rvn-table">
        <thead>
          <tr>
            <th>Département</th>
            <th className="rvn-num">Volume <span className="mar-th-sub">· évol. N-1</span></th>
            <th className="rvn-num">Part</th>
            <th>Marque leader</th>
            <th className="rvn-num">Part marque leader</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i}>
              <td className="rvn-marque-cell"><span className="mar-label-txt">{r.label}</span></td>
              <td className="rvn-num"><VolCell volume={r.volume} delta={r.delta_volume} /></td>
              <td className="rvn-num">
                <span className="rvn-part">
                  <span className="rvn-part-bar" style={{ width: `${Math.min(r.part || 0, 100).toFixed(1)}%` }} />
                  <span className="rvn-part-txt">{fmtPct(r.part)}</span>
                </span>
              </td>
              <td>{r.marque_leader ? r.marque_leader.marque : '—'}</td>
              <td className="rvn-num">{r.marque_leader ? fmtPct(r.marque_leader.part) : '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// « Nos marques » : matrice marque (lignes) × département (colonnes). Bascule
// Parts % / Volumes. Par ligne, meilleur département en vert / moins bon en rouge.
// Drill-down marque ▸ modèle ▸ type de client.
function NosMarquesMatrix({ data }) {
  const [open, setOpen] = useState({});     // marques dépliées
  const [openM, setOpenM] = useState({});   // modèles dépliés (clé marque∥modele)
  const [mode, setMode] = useState('part'); // 'part' | 'volume'
  if (!data || !data.rows || !data.departements || data.departements.length === 0) return null;
  const depts = data.departements;
  const toggle = (mq) => setOpen(o => ({ ...o, [mq]: !o[mq] }));
  const toggleM = (k) => setOpenM(o => ({ ...o, [k]: !o[k] }));
  const val = (c) => (mode === 'volume' ? c.volume : c.part);
  const fmt = (c) => { const v = val(c); if (v == null) return '—'; return mode === 'volume' ? fmtNumber(v) : fmtPct(v); };
  // Cellules d'une ligne : meilleur (vert) / moins bon (rouge) sur la métrique affichée.
  const renderCells = (cells, keyPrefix) => {
    const vals = cells.map(val);
    const present = vals.filter(v => v != null);
    const max = present.length ? Math.max(...present) : null;
    const min = present.length ? Math.min(...present) : null;
    const distinct = max !== min;
    return cells.map((c, i) => {
      const v = vals[i]; let cls = '';
      if (distinct && v != null) { if (v === max) cls = 'mar-best'; else if (v === min) cls = 'mar-worst'; }
      return <td key={keyPrefix + c.departement} className={`rvn-num ${cls}`} title={`${c.departement} · ${fmtNumber(c.volume)} immat.`}>{fmt(c)}</td>;
    });
  };
  return (
    <div className="mar-monthly">
      <div className="mar-monthly-toggle">
        <button className={mode === 'part' ? 'active' : ''} onClick={() => setMode('part')}>Parts de marché %</button>
        <button className={mode === 'volume' ? 'active' : ''} onClick={() => setMode('volume')}>Volumes</button>
      </div>
      <div className="rvn-table-wrap mar-monthly-wrap">
        <table className="rvn-table mar-monthly-table">
          <thead>
            <tr>
              <th className="mar-sticky-col">Marque</th>
              {depts.map(d => <th key={d} className="rvn-num">{d}</th>)}
            </tr>
          </thead>
          <tbody>
            {data.rows.map(row => {
              const modeles = row.modeles || [];
              const isOpen = !!open[row.marque];
              return (
                <React.Fragment key={row.marque}>
                  <tr className={`mar-groupe-row ${isOpen ? 'mar-groupe-open' : ''}`} onClick={() => modeles.length && toggle(row.marque)}>
                    <td className="rvn-marque-cell mar-sticky-col">
                      <span className="mar-caret-inline">{modeles.length ? (isOpen ? '▾' : '▸') : ''}</span>{row.marque}
                    </td>
                    {renderCells(row.cells, 'm')}
                  </tr>
                  {isOpen && modeles.map(md => {
                    const clients = md.clients || [];
                    const mKey = row.marque + '' + md.label;
                    const mOpen = !!openM[mKey];
                    return (
                      <React.Fragment key={md.label}>
                        <tr className={`mar-submarque-row ${mOpen ? 'mar-submarque-open' : ''}`} onClick={() => clients.length && toggleM(mKey)}>
                          <td className="mar-sticky-col mar-submarque-label">
                            <span className="mar-caret-inline">{clients.length ? (mOpen ? '▾' : '▸') : ''}</span>{md.label}
                          </td>
                          {renderCells(md.cells, mKey)}
                        </tr>
                        {mOpen && clients.map(cl => (
                          <tr key={cl.label} className="mar-subclient-row">
                            <td className="mar-sticky-col mar-subclient-label">{cl.label}</td>
                            {renderCells(cl.cells, mKey + cl.label)}
                          </tr>
                        ))}
                      </React.Fragment>
                    );
                  })}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// Évolution mensuelle — multi-dimensions. Bascule de dimension (marque / modèle /
// groupe / type de client) + bascule métrique (Parts % / Volumes). Entités en
// lignes, mois en colonnes (1re colonne figée), ligne « Total marché » en pied.
function MonthlyTable({ evolution }) {
  const [metric, setMetric] = useState('part'); // 'part' | 'volume'
  const [dim, setDim] = useState('marque');      // 'marque' | 'modele' | 'groupe' | 'client'
  if (!evolution || !evolution.mois || evolution.mois.length === 0) {
    return <div className="rpa-empty">Aucune donnée mensuelle. Élargissez la période pour voir l'évolution.</div>;
  }
  const mois = evolution.mois;
  const { entities = [], values = {} } = (evolution.dims && evolution.dims[dim]) || {};
  const DIM_LABEL = { marque: 'Marque', modele: 'Modèle', groupe: 'Groupe', client: 'Type de client', origine: 'Origine', constructeur: 'Constructeur' };
  const moisLabel = (ym) => {
    const [y, mo] = ym.split('-');
    const d = new Date(Number(y), Number(mo) - 1, 1);
    return d.toLocaleDateString('fr-FR', { month: 'short', year: '2-digit' });
  };
  const cell = (entity, m) => {
    const v = (values[m] || {})[entity] || 0;
    const tot = evolution.volume_total[m] || 0;
    return metric === 'volume' ? fmtNumber(v) : (tot ? fmtPct(Math.round((v / tot) * 1000) / 10) : '—');
  };
  return (
    <div className="mar-monthly">
      <div className="mar-monthly-toolbar">
        <div className="mar-monthly-toggle">
          <button className={dim === 'marque' ? 'active' : ''} onClick={() => setDim('marque')}>Par marque</button>
          <button className={dim === 'modele' ? 'active' : ''} onClick={() => setDim('modele')}>Par modèle</button>
          <button className={dim === 'groupe' ? 'active' : ''} onClick={() => setDim('groupe')}>Par groupe</button>
          <button className={dim === 'client' ? 'active' : ''} onClick={() => setDim('client')}>Par type de client</button>
          <button className={dim === 'origine' ? 'active' : ''} onClick={() => setDim('origine')}>Par origine</button>
          <button className={dim === 'constructeur' ? 'active' : ''} onClick={() => setDim('constructeur')}>Par constructeur</button>
        </div>
        <div className="mar-monthly-toggle">
          <button className={metric === 'part' ? 'active' : ''} onClick={() => setMetric('part')}>Parts de marché %</button>
          <button className={metric === 'volume' ? 'active' : ''} onClick={() => setMetric('volume')}>Volumes</button>
        </div>
      </div>
      <div className="rvn-table-wrap mar-monthly-wrap">
        <table className="rvn-table mar-monthly-table">
          <thead>
            <tr>
              <th className="mar-sticky-col">{DIM_LABEL[dim]}</th>
              {mois.map(m => <th key={m} className="rvn-num">{moisLabel(m)}</th>)}
            </tr>
          </thead>
          <tbody>
            {entities.map(ent => (
              <tr key={ent}>
                <td className="rvn-marque-cell mar-sticky-col">{ent}</td>
                {mois.map(m => <td key={m} className="rvn-num">{cell(ent, m)}</td>)}
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="rvn-total-row">
              <td className="mar-sticky-col">Total marché</td>
              {mois.map(m => (
                <td key={m} className="rvn-num">{metric === 'volume' ? fmtNumber(evolution.volume_total[m] || 0) : '100 %'}</td>
              ))}
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}

// Bloc unique « Parts de marché » : un toggle choisit la dimension de 1er niveau.
//   Par marque         : marque ▸ modèle ▸ type de client (Drilldown3Table)
//   Par modèle         : modèle ▸ type de client (Drilldown2Table, top 100)
//   Par groupe         : groupe ▸ marque ▸ type de client (Drilldown3Table)
//   Par type de client : type de client ▸ marque (Drilldown2Table, sans classement)
function PartsBlock({ parMarque, parModele, parModeleTotal, parGroupe, parClient, parOrigine, parConstructeur }) {
  const [mode, setMode] = useState('marque'); // marque | modele | groupe | client | origine | constructeur
  return (
    <>
      <div className="mar-monthly-toggle">
        <button className={mode === 'marque' ? 'active' : ''} onClick={() => setMode('marque')}>Par marque</button>
        <button className={mode === 'modele' ? 'active' : ''} onClick={() => setMode('modele')}>Par modèle</button>
        <button className={mode === 'groupe' ? 'active' : ''} onClick={() => setMode('groupe')}>Par groupe</button>
        <button className={mode === 'client' ? 'active' : ''} onClick={() => setMode('client')}>Par type de client</button>
        <button className={mode === 'origine' ? 'active' : ''} onClick={() => setMode('origine')}>Par origine</button>
        <button className={mode === 'constructeur' ? 'active' : ''} onClick={() => setMode('constructeur')}>Par constructeur</button>
      </div>
      {mode === 'marque' && (
        <Drilldown3Table rows={parMarque} entityLabel="Marque" childKey="modeles" grandChildKey="clients" withRank />
      )}
      {mode === 'modele' && (
        <>
          <Drilldown2Table rows={parModele} entityLabel="Modèle" childKey="clients" withRank />
          {parModeleTotal > parModele.length && (
            <div className="rpa-block-sub" style={{ marginTop: 8 }}>Top {parModele.length} modèles affichés sur {parModeleTotal}.</div>
          )}
        </>
      )}
      {mode === 'groupe' && (
        <Drilldown3Table rows={parGroupe} entityLabel="Groupe" childKey="marques" grandChildKey="clients" withRank />
      )}
      {mode === 'client' && (
        <Drilldown2Table rows={parClient} entityLabel="Type de client" childKey="marques" />
      )}
      {mode === 'origine' && (
        <Drilldown3Table rows={parOrigine} entityLabel="Origine" childKey="marques" grandChildKey="clients" withRank />
      )}
      {mode === 'constructeur' && (
        <Drilldown3Table rows={parConstructeur} entityLabel="Constructeur" childKey="marques" grandChildKey="clients" withRank />
      )}
    </>
  );
}

// Modale Référentiel marché (super-admin) : onglet Marques (constructeur + origine)
// et onglet Affectations (par DÉPARTEMENT : toutes les marques, groupe affecté,
// concession, + historique dépliable des groupes).
function ConfigMarcheModal({ onClose, onSaved }) {
  const [tab, setTab] = useState('marques');
  const [marques, setMarques] = useState([]);
  const [constructeurs, setConstructeurs] = useState([]);
  const [presets, setPresets] = useState([]);
  const [departements, setDepartements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [aDept, setADept] = useState('');
  const [affList, setAffList] = useState([]);
  const [groupes, setGroupes] = useState([]);
  const [aLoading, setALoading] = useState(false);
  const [aSearch, setASearch] = useState('');

  const reload = () => reportingFinancierApi.getMarcheConfig().then(r => {
    setMarques(r.data?.marques || []); setConstructeurs(r.data?.constructeurs || []);
    setPresets(r.data?.origines_presets || []); setDepartements(r.data?.departements || []);
  }).catch(() => {}).finally(() => setLoading(false));
  useEffect(() => { reload(); }, []);

  const setMarqueField = (mq, field, val) => setMarques(list => list.map(m => m.marque === mq ? { ...m, [field]: val } : m));
  const saveMarques = async () => {
    setSaving(true);
    try { await reportingFinancierApi.saveMarcheMarques(marques); if (onSaved) onSaved(); onClose(); }
    catch (e) { alert(e.response?.data?.error || 'Erreur'); } finally { setSaving(false); }
  };

  const loadDept = (dep) => {
    if (!dep) { setAffList([]); return; }
    setALoading(true);
    reportingFinancierApi.getMarcheAffectations(undefined, dep)
      .then(r => { setAffList((r.data?.marques || []).map(m => ({ ...m, _open: false, _dirty: false }))); setGroupes(r.data?.groupes || []); })
      .catch(() => {}).finally(() => setALoading(false));
  };
  const updRow = (mq, patch) => setAffList(list => list.map(r => r.marque === mq ? { ...r, ...patch, _dirty: true } : r));
  // Groupe courant = période ouverte (date_fin vide). On l'édite directement sans
  // toucher à l'historique ; on ne crée une période datée que pour un changement.
  const groupeCourant = (periodes) => {
    const open = periodes.find(p => !p.date_fin);
    return open ? open.groupe : (periodes.length ? periodes[periodes.length - 1].groupe : '');
  };
  const updGroupeCourant = (mq, val) => setAffList(list => list.map(r => {
    if (r.marque !== mq) return r;
    let periodes = [...r.periodes];
    const openIdx = periodes.findIndex(p => !p.date_fin);
    if (openIdx >= 0) periodes = periodes.map((p, j) => j === openIdx ? { ...p, groupe: val } : p);
    else if (val) periodes = [...periodes, { groupe: val, date_debut: '2019-01-01', date_fin: '' }];
    return { ...r, groupe_courant: val, periodes, _dirty: true };
  }));
  const updPeriode = (mq, i, field, val) => setAffList(list => list.map(r => {
    if (r.marque !== mq) return r;
    const periodes = r.periodes.map((p, j) => j === i ? { ...p, [field]: val } : p);
    return { ...r, periodes, groupe_courant: groupeCourant(periodes), _dirty: true };
  }));
  const addPeriode = (mq) => setAffList(list => list.map(r => r.marque === mq ? { ...r, periodes: [...r.periodes, { groupe: '', date_debut: '', date_fin: '' }], _dirty: true, _open: true } : r));
  const removePeriode = (mq, i) => setAffList(list => list.map(r => r.marque === mq ? { ...r, periodes: r.periodes.filter((_, j) => j !== i), _dirty: true } : r));
  const toggleOpen = (mq) => setAffList(list => list.map(r => r.marque === mq ? { ...r, _open: !r._open } : r));

  const saveRow = async (row) => {
    setSaving(true);
    try {
      await reportingFinancierApi.saveMarcheAffectations({ marque: row.marque, departement: aDept, periodes: row.periodes, concession: row.concession });
      const open = row.periodes.find(p => !p.date_fin);
      const gc = open ? open.groupe : (row.periodes.length ? row.periodes[row.periodes.length - 1].groupe : '');
      setAffList(list => list.map(r => r.marque === row.marque ? { ...r, groupe_courant: gc, _dirty: false } : r));
      if (onSaved) onSaved();
    } catch (e) { alert(e.response?.data?.error || 'Erreur'); } finally { setSaving(false); }
  };

  const doBootstrap = async () => {
    if (!window.confirm('Réinitialiser tout le référentiel depuis les données (colonnes P/Q/R) ? Les personnalisations seront écrasées.')) return;
    setSaving(true);
    try { const r = await reportingFinancierApi.bootstrapMarche(); await reload(); if (aDept) loadDept(aDept); if (onSaved) onSaved(); alert(`Initialisé : ${r.data.marques} marques, ${r.data.periodes} périodes, ${r.data.concessions} concessions.`); }
    catch (e) { alert(e.response?.data?.error || 'Erreur'); } finally { setSaving(false); }
  };

  const filtered = marques.filter(m => m.marque.toLowerCase().includes(search.toLowerCase()));
  const allOrigines = [...new Set([...(presets || []), ...marques.map(m => m.origine).filter(Boolean)])];
  const affFiltered = affList.filter(r => r.marque.toLowerCase().includes(aSearch.toLowerCase()));

  return (
    <div className="modal-overlay">
      <div className="modal-content large" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Référentiel marché</h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          <div className="page-tabs">
            <button className={tab === 'marques' ? 'active' : ''} onClick={() => setTab('marques')}>Marques</button>
            <button className={tab === 'affectations' ? 'active' : ''} onClick={() => setTab('affectations')}>Affectations (groupe / concession)</button>
          </div>
          {loading ? <div className="rpa-loading">Chargement…</div> : tab === 'marques' ? (
            <>
              <input className="page-search" placeholder="🔍 Rechercher une marque..." value={search} onChange={e => setSearch(e.target.value)} />
              <table className="rvn-table mar-config-table">
                <thead><tr><th>Marque</th><th>Constructeur</th><th>Origine (nationalité)</th></tr></thead>
                <tbody>
                  {filtered.map(m => (
                    <tr key={m.marque}>
                      <td>{m.marque}</td>
                      <td><input className="page-select" list="mar-constructeurs" value={m.constructeur || ''} onChange={e => setMarqueField(m.marque, 'constructeur', e.target.value)} /></td>
                      <td>
                        <select className="page-select" value={m.origine || ''} onChange={e => setMarqueField(m.marque, 'origine', e.target.value)}>
                          <option value="">—</option>
                          {allOrigines.map(o => <option key={o} value={o}>{o}</option>)}
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <datalist id="mar-constructeurs">{constructeurs.map(c => <option key={c} value={c} />)}</datalist>
            </>
          ) : (
            <>
              <div className="mar-aff-pick">
                <select className="page-select" value={aDept} onChange={e => { setADept(e.target.value); loadDept(e.target.value); }}>
                  <option value="">Choisir un département…</option>
                  {departements.map(d => <option key={d} value={d}>{d}</option>)}
                </select>
                {aDept && <input className="page-search" placeholder="🔍 Filtrer une marque..." value={aSearch} onChange={e => setASearch(e.target.value)} />}
              </div>
              {!aDept ? <div className="rpa-empty">Choisissez un département pour voir toutes les marques.</div>
                : aLoading ? <div className="rpa-loading">Chargement…</div> : (
                  <table className="rvn-table mar-config-table mar-aff-table">
                    <thead><tr><th></th><th>Marque</th><th>Groupe affecté</th><th>Concession</th><th></th></tr></thead>
                    <tbody>
                      {affFiltered.map(row => (
                        <React.Fragment key={row.marque}>
                          <tr>
                            <td className="mar-caret" style={{ cursor: 'pointer' }} onClick={() => toggleOpen(row.marque)}>{row._open ? '▾' : '▸'}</td>
                            <td>{row.marque}</td>
                            <td>
                              <input className="page-select" list="mar-groupes" value={row.groupe_courant || ''} placeholder="—" onChange={e => updGroupeCourant(row.marque, e.target.value)} />
                              {row.periodes.length > 1 && <span className="mar-nb-marques" title="Historique de changements">{row.periodes.length} périodes</span>}
                            </td>
                            <td><input className="page-select" value={row.concession || ''} onChange={e => updRow(row.marque, { concession: e.target.value })} placeholder="—" /></td>
                            <td><button className="btn-primary mar-row-save" disabled={!row._dirty || saving} title="Enregistrer cette marque" onClick={() => saveRow(row)}>💾</button></td>
                          </tr>
                          {row._open && (
                            <tr className="mar-history-row">
                              <td></td>
                              <td colSpan={4}>
                                <div className="rpa-block-sub" style={{ margin: '4px 0' }}>Historique des groupes (laisser « Fin » vide = période en cours) :</div>
                                <table className="rvn-table mar-config-table">
                                  <thead><tr><th>Groupe</th><th>Début</th><th>Fin</th><th></th></tr></thead>
                                  <tbody>
                                    {row.periodes.map((p, i) => (
                                      <tr key={i}>
                                        <td><input className="page-select" list="mar-groupes" value={p.groupe || ''} onChange={e => updPeriode(row.marque, i, 'groupe', e.target.value)} /></td>
                                        <td><input type="date" className="page-select" value={p.date_debut || ''} onChange={e => updPeriode(row.marque, i, 'date_debut', e.target.value)} /></td>
                                        <td><input type="date" className="page-select" value={p.date_fin || ''} onChange={e => updPeriode(row.marque, i, 'date_fin', e.target.value)} /></td>
                                        <td><button className="btn-icon delete" title="Supprimer" onClick={() => removePeriode(row.marque, i)}>🗑️</button></td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                                <button className="page-select" onClick={() => addPeriode(row.marque)}>+ Ajouter une période</button>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                )}
              <datalist id="mar-groupes">{groupes.map(g => <option key={g} value={g} />)}</datalist>
            </>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn-secondary" onClick={doBootstrap} disabled={saving}>↻ Initialiser depuis les données</button>
          <span style={{ flex: 1 }} />
          <button className="btn-secondary" onClick={onClose}>Fermer</button>
          {tab === 'marques' && <button className="btn-primary" onClick={saveMarques} disabled={saving}>{saving ? '…' : 'Enregistrer les marques'}</button>}
        </div>
      </div>
    </div>
  );
}

function ReportingMarche({ dateDebut, dateFin, user }) {
  const [departements, setDepartements] = useState([]); // sélection
  const [deptOptions, setDeptOptions] = useState([]);
  const [vpvu, setVpvu] = useState(''); // '' = tous, 'VP', 'VUL'
  const [showConfig, setShowConfig] = useState(false);
  const isSuperAdmin = user?.role === 'super-admin';

  const { data, loading, error, reload } = useReportingData(
    (signal) => reportingFinancierApi.getMarcheKpis(
      { date_debut: dateDebut, date_fin: dateFin, ...(departements.length ? { departement: departements } : {}), ...(vpvu ? { vpvu } : {}) },
      signal,
    ).then(r => r.data),
    [dateDebut, dateFin, JSON.stringify(departements), vpvu],
    { enabled: !!(dateDebut && dateFin) },
  );

  useEffect(() => {
    reportingFinancierApi.getMarcheDimensions()
      .then(r => setDeptOptions(r.data?.departements || []))
      .catch(() => {});
  }, []);

  const g = data?.kpis_globaux || {};
  const deptScope = departements.length === 0 ? 'tous départements' : departements.length === 1 ? departements[0] : `${departements.length} départements`;

  return (
    <div className="rpa-dashboard rf-surface">
      <UpdatingOverlay show={loading} />
      <div className="rpa-dashboard-header">
        <div className="rpa-dashboard-title">
          <span className="rpa-dashboard-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 3v18h18" />
              <path d="M7 15l4-5 3 3 5-7" />
            </svg>
          </span>
          <div>
            <h2>Marché — Immatriculations</h2>
            <div className="rpa-dashboard-subtitle">
              Du {new Date(dateDebut).toLocaleDateString('fr-FR')} au {new Date(dateFin).toLocaleDateString('fr-FR')}
              {' · '}{deptScope}{vpvu ? ` · ${vpvu}` : ' · VP + VUL'}
            </div>
          </div>
        </div>
        <div className="rpa-toolbar mar-toolbar">
          <DeptSelect options={deptOptions} selected={departements} onChange={setDepartements} />
          <select value={vpvu} onChange={e => setVpvu(e.target.value)} className={`page-select ${vpvu ? 'active-filter' : ''}`}>
            <option value="">VP + VUL</option>
            <option value="VP">VP (particuliers)</option>
            <option value="VUL">VUL (utilitaires)</option>
          </select>
          {isSuperAdmin && (
            <button type="button" className="page-select" onClick={() => setShowConfig(true)} title="Référentiel marché (constructeur, origine, groupe, concession)">⚙️ Référentiel</button>
          )}
        </div>
      </div>

      {showConfig && <ConfigMarcheModal onClose={() => setShowConfig(false)} onSaved={reload} />}

      {error && !data && <div className="rpa-error">⚠️ {error}</div>}

      {!data ? (
        <div className="rpa-loading">Chargement…</div>
      ) : (
        <>
          <div className="rpa-hero-grid">
            <KpiHero
              label="Volume marché"
              value={fmtNumber(g.volume_total)}
              sublabel={<><DeltaBadge value={g.delta_volume_pct} /> vs N-1 ({fmtNumber(g.volume_total_n1)})</>}
            />
            <KpiHero
              label="Marque leader"
              value={g.marque_leader ? g.marque_leader.label : '—'}
              sublabel={g.marque_leader
                ? <>{fmtPct(g.marque_leader.part)} de part · <DeltaBadge value={g.marque_leader.delta_part} unit="pt" /></>
                : ' '}
            />
            <KpiHero
              label="Groupe leader"
              value={g.groupe_leader ? g.groupe_leader.label : '—'}
              sublabel={g.groupe_leader
                ? <>{fmtPct(g.groupe_leader.part)} de part · <DeltaBadge value={g.groupe_leader.delta_part} unit="pt" /></>
                : ' '}
            />
            <KpiHero label="Marques" value={fmtNumber(g.nb_marques)} sublabel="présentes" />
            <KpiHero label="Groupes" value={fmtNumber(g.nb_groupes)} sublabel="rattachés" />
          </div>

          {/* Vue par département — toujours affichée en premier */}
          <div className="rpa-dim-block">
            <div className="rpa-block-header">
              <h3>Vue par département</h3>
              <span className="rpa-block-sub">volume · part · marque leader</span>
            </div>
            <DepartementTable rows={data?.par_departement || []} />
          </div>

          {/* Parts de marché — bloc unique, dimension au choix (marque / modèle / groupe / type de client) */}
          <div className="rpa-dim-block">
            <div className="rpa-block-header">
              <h3>Parts de marché</h3>
              <span className="rpa-block-sub">par marque · modèle · groupe · type de client · origine (drill-down)</span>
            </div>
            <PartsBlock
              parMarque={data?.par_marque || []}
              parModele={data?.par_modele || []}
              parModeleTotal={data?.par_modele_total || 0}
              parGroupe={data?.par_groupe || []}
              parClient={data?.par_client || []}
              parOrigine={data?.par_origine || []}
              parConstructeur={data?.par_constructeur || []}
            />
          </div>

          {/* Évolution mensuelle (parts % / volumes par marque) */}
          <div className="rpa-dim-block">
            <div className="rpa-block-header">
              <h3>Évolution mensuelle</h3>
              <span className="rpa-block-sub">par marque · modèle · groupe · type de client — parts % ou volumes</span>
            </div>
            <MonthlyTable evolution={data?.evolution || null} />
          </div>

          {/* Nos marques — part de marché par département (tous les départements,
              quel que soit le filtre) — bloc en bas de page */}
          <div className="rpa-dim-block">
            <div className="rpa-block-header">
              <h3>Nos marques</h3>
              <span className="rpa-block-sub">part de marché par département (tous les départements) · mieux / moins bien selon le territoire</span>
            </div>
            <NosMarquesMatrix data={data?.nos_marques} />
          </div>
        </>
      )}
    </div>
  );
}

export default ReportingMarche;
